import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useProducts } from "@/hooks/useProducts";
import { ProductDrawer } from "./price-book/ProductDrawer";
import { toast } from "@/hooks/use-toast";
import { Palette, Plus, Edit, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SHINGLE_TYPES = [
  { key: 'uhdz', label: 'UHDZ', description: 'Ultra High Definition' },
  { key: 'hdz', label: 'HDZ', description: 'High Definition' },
  { key: 'slateline', label: 'Slateline', description: 'Designer Slate' },
  { key: 'camelot', label: 'Camelot', description: 'Designer Premium' },
];

export function ShingleColorManager() {
  const [selectedType, setSelectedType] = useState('uhdz');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);

  const { products = [], updateProduct, deleteProduct } = useProducts();

  // Filter colors for selected type
  const colors = products.filter(
    (p) =>
      p.category === 'Shingle Colors' &&
      p.subcategory?.toLowerCase() === selectedType.toLowerCase() &&
      p.install_phase === 'Phase 11.5: Shingle Color'
  ).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  const handleToggleActive = async (productId: string, currentStatus: boolean) => {
    try {
      await updateProduct.mutateAsync({
        id: productId,
        is_active: !currentStatus,
      });
      toast({
        title: "Color Updated",
        description: `Color ${!currentStatus ? 'enabled' : 'disabled'} successfully`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to update color",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (product: any) => {
    setEditingProduct(product);
    setDrawerOpen(true);
  };

  const handleDeleteClick = (productId: string) => {
    setProductToDelete(productId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    try {
      await deleteProduct.mutateAsync(productToDelete);
      toast({
        title: "Color Deleted",
        description: "Shingle color removed successfully",
      });
    } catch (error: any) {
      toast({
        title: "Failed to delete color",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const handleAddNew = () => {
    setEditingProduct({
      category: 'Shingle Colors',
      subcategory: selectedType,
      install_phase: 'Phase 11.5: Shingle Color',
      unit: 'square',
      brand: 'GAF',
      tier: selectedType === 'uhdz' || selectedType === 'slateline' || selectedType === 'camelot' ? 'best' : 'better',
    });
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setEditingProduct(null);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              <div>
                <CardTitle>Shingle Color Management</CardTitle>
                <CardDescription>
                  Manage available colors for each shingle type
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add Color
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedType} onValueChange={setSelectedType}>
            <TabsList className="grid w-full grid-cols-4">
              {SHINGLE_TYPES.map((type) => (
                <TabsTrigger key={type.key} value={type.key}>
                  {type.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {SHINGLE_TYPES.map((type) => (
              <TabsContent key={type.key} value={type.key} className="space-y-4">
                <div className="text-sm text-muted-foreground mb-4">
                  {type.description} • {colors.length} colors available
                </div>

                {colors.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No colors configured for {type.label}.
                    <Button
                      variant="link"
                      onClick={handleAddNew}
                      className="ml-2"
                    >
                      Add your first color
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {colors.map((color) => (
                      <Card key={color.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <Switch
                              checked={color.is_active}
                              onCheckedChange={() =>
                                handleToggleActive(color.id, color.is_active)
                              }
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{color.name}</span>
                                {!color.is_active && (
                                  <Badge variant="secondary">Disabled</Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {color.customer_description || color.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(color)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteClick(color.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      <ProductDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        product={editingProduct}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shingle Color?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this color option. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
